import { useEnumHelpers } from '@/queries/useEnumHelpers';
import { Construction } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { IconWrapper, type IconKind } from '../components/icon/IconWrapper.tsx';
import { BuildingParameters } from '../stores/SubprojectStore.ts';
import { formattedNumber } from '../utils.ts';

interface DecarbHubBuildingDetailsProps {
  buildingParams?: BuildingParameters;
  designLifeDisplay?: string | null;
  newOrRenewalDisplay?: string | null;
}

export const DecarbHubBuildingDetails: React.FC<
  DecarbHubBuildingDetailsProps
> = ({ buildingParams, designLifeDisplay, newOrRenewalDisplay }) => {
  const { areaUnits } = useEnumHelpers();

  const areaSymbol = areaUnits.find(
    (au) => au.name === buildingParams?.conditionedSpace?.unit,
  )?.symbol;

  const conditionedSpaceValue = buildingParams?.conditionedSpace?.value
    ? `${formattedNumber(buildingParams.conditionedSpace.value)} ${areaSymbol ?? ''}`
    : null;

  const floorsValue = buildingParams?.numberOfFloors
    ? `${buildingParams.numberOfFloors} Stories`
    : null;

  const hasAnything =
    conditionedSpaceValue ||
    floorsValue ||
    designLifeDisplay ||
    newOrRenewalDisplay;

  if (!hasAnything) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        columnGap: 3,
        rowGap: 1.5,
        p: 4,
      }}
    >
      <DetailItem
        iconKind='HVAC'
        value={conditionedSpaceValue}
        tooltip='Conditioned Space'
      />
      <DetailItem iconKind='Floors' value={floorsValue} tooltip='Floors' />
      <DetailItem
        iconKind='Floors'
        value={designLifeDisplay ?? null}
        tooltip='Design Life'
      />
      <DetailItem
        muiIcon={
          <Construction
            sx={{ width: 18, height: 18, color: 'text.secondary' }}
          />
        }
        value={newOrRenewalDisplay ?? null}
        tooltip='Construction Type'
      />
    </Box>
  );
};

const DetailItem: React.FC<{
  iconKind?: IconKind;
  muiIcon?: React.ReactNode;
  value: string | null;
  tooltip: string;
}> = ({ iconKind, muiIcon, value, tooltip }) => (
  <Tooltip title={tooltip} arrow placement='top'>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minWidth: 0,
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        ...(value == null && { opacity: 0.4 }),
      }}
    >
      {iconKind ? (
        <IconWrapper
          icon={iconKind}
          sx={{ width: 18, height: 18 }}
          color='info'
        />
      ) : (
        muiIcon
      )}
      <Typography
        variant='caption'
        sx={{
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          color: 'common.white',
          lineHeight: 1,
          alignSelf: 'center',
        }}
      >
        {value ?? '\u2014'}
      </Typography>
    </Box>
  </Tooltip>
);
